import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

import { useApi } from 'landing-page/hooks/useApi';
import { api, BlogSectionDataType, BlogItemType } from 'landing-page/helpers/api';

import Container from 'landing-page/components/common/Container';
import SectionHeader from 'landing-page/components/home/SectionHeader';
import HighlightCard from 'landing-page/components/common/HighlightCard'

import styles from './Blog.module.scss';

interface BlogProps {
    items?: BlogSectionDataType,
    autoFetch?: boolean
}

const BlogPlaceholder: React.FC = () => (
    <Container>
        <SectionHeader
            title="Berita Terbaru"
            subtitle="Berita & Blog Terbaru"
            withDescriptionAnimation={false}
            withTitleAnimation={true}
        />

        <div className='row'>
            {/* Large card placeholder on the left */}
            <div className='col-md-6'>
                <Card className='border-0 shadow-sm rounded-4 overflow-hidden h-100 blog-card'>
                    <div className='position-relative h-100'>
                        <div className='bg-light h-100 w-100' style={{ minHeight: '400px' }}>
                            <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'>
                                <div className='spinner-border text-primary' role='status'>
                                    <span className='visually-hidden'>Loading...</span>
                                </div>
                            </div>
                        </div>
                        {/* Dark overlay */}
                        <div 
                            className='position-absolute top-0 start-0 w-100 h-100' 
                            style={{ 
                                background: '#f5f5f5',
                                zIndex: 1
                            }}
                        ></div>
                    
                        {/* Content overlay using Card.Body */}
                        <Card.Body className='position-absolute top-0 start-0 w-100 h-100 text-white d-flex flex-column justify-content-between' style={{ zIndex: 2, background: 'transparent' }}>
                            {/* Date tag placeholder at top */}
                            <div className='d-flex flex-column justify-content-center bg-primary text-white p-2 rounded-2' style={{ width: '56px', height: '56px' }}>
                                <div className='placeholder-glow'>
                                    <div className='placeholder bg-white bg-opacity-75 rounded' style={{ height: '20px', width: '40px' }}></div>
                                    <div className='placeholder bg-white bg-opacity-75 rounded mt-1' style={{ height: '15px', width: '30px' }}></div>
                                </div>
                            </div>
                            
                            {/* Content placeholder at bottom */}
                            <div>
                                <div className='d-flex align-items-center gap-2 mb-2'>
                                    <i className='bi bi-tag text-primary'></i>
                                    <div className='placeholder-glow'>
                                        <div className='placeholder bg-white bg-opacity-75 rounded' style={{ height: '12px', width: '80px' }}></div>
                                    </div>
                                </div>
                                <div className='placeholder-glow'>
                                    <div className='placeholder bg-white bg-opacity-75 rounded mb-2' style={{ height: '24px', width: '100%' }}></div>
                                    <div className='placeholder bg-white bg-opacity-75 rounded' style={{ height: '24px', width: '70%' }}></div>
                                </div>
                            </div>
                        </Card.Body>
                    </div>
                </Card>
            </div>
            {/* Small cards placeholder */}
            <div className='col-md-6'>
                <div className='row gap-5 h-100 d-flex flex-column justify-content-between align-items-center'>
                    {[1, 2].map((index) => (
                        <div className='col' key={index}>
                            <Card className='border-0 shadow-sm rounded-4 overflow-hidden h-100 blog-card'>
                                <div className='row g-0 h-100'>
                                    <div className='col-5'>
                                        <div className='position-relative h-100'>
                                            <div className='bg-light h-100 w-100' style={{ minHeight: '120px' }}>
                                                <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'>
                                                    <div className='spinner-border spinner-border-sm text-primary' role='status'>
                                                        <span className='visually-hidden'>Loading...</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Dark overlay */}
                                            <div 
                                                className='position-absolute top-0 start-0 w-100 h-100' 
                                                style={{ 
                                                    background: '#f5f5f5',
                                                    zIndex: 1
                                                }}
                                            ></div>
                                            
                                            {/* Date tag placeholder */}
                                            <div className='position-absolute top-0 start-0 m-2' style={{ zIndex: 2 }}>
                                                <div className='bg-primary text-white p-2 rounded-2 small fw-bold' style={{ width: '42px', height: '42px' }}>
                                                    <div className='placeholder-glow'>
                                                        <div className='placeholder bg-white bg-opacity-75 rounded' style={{ height: '12px', width: '26px' }}></div>
                                                        <div className='placeholder bg-white bg-opacity-75 rounded mt-1' style={{ height: '10px', width: '20px' }}></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col-7'>
                                        <Card.Body className='d-flex flex-column h-100'>
                                            <div className='d-flex align-items-center gap-2 mb-2'>
                                                <i className='bi bi-tag text-primary'></i>
                                                <div className='placeholder-glow'>
                                                    <div className='placeholder bg-secondary rounded' style={{ height: '12px', width: '60px' }}></div>
                                                </div>
                                            </div>
                                            <div className='placeholder-glow'>
                                                <div className='placeholder bg-secondary rounded mb-2' style={{ height: '16px', width: '100%' }}></div>
                                                <div className='placeholder bg-secondary rounded mb-2' style={{ height: '16px', width: '90%' }}></div>
                                                <div className='placeholder bg-secondary rounded' style={{ height: '16px', width: '60%' }}></div>
                                            </div>
                                            <div className='mt-auto'>
                                                <div className='placeholder-glow'>
                                                    <div className='placeholder bg-primary rounded' style={{ height: '16px', width: '100px' }}></div>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    ))}
                </div>  
            </div>
        </div>
    </Container>
)

const Blog: React.FC<BlogProps> = ({
    items: propItems = null,
    autoFetch = true
}) => {

    const [blogData, setBlogData] = useState<BlogSectionDataType | null>(null);

    const { data, execute: fetchBlogData, loading } = useApi(api.getBlogSection.bind(api));

    useEffect(() => {
        if (autoFetch && propItems === null) {
            fetchBlogData();
        } else if (propItems !== null) {
            setBlogData(propItems);
        }
    }, [autoFetch, propItems]);

    useEffect(() => {
        if (data?.data) {
            setBlogData(data.data as unknown as BlogSectionDataType);
        }
    }, [data]);

    // Show placeholder while loading or if no data
    if (loading || (!blogData && autoFetch)) {
        return <BlogPlaceholder />;
    }

    // Show placeholder if no data is available
    if (!blogData) {
        return <BlogPlaceholder />;
    }

    return (
        <Container>
            <SectionHeader
                title="Berita Terbaru"
                subtitle="Berita & Blog Terbaru"
                withDescriptionAnimation={false}
                withTitleAnimation={true}
                withLink={{
                    text: 'Lihat Lebih Lengkap',
                    url: '/blog'
                }}
            />

            <div className='row gap-4 gap-md-0'>
                {/* Large card on the left */}
                <div className='col-md-6'>
                    <HighlightCard
                        title={blogData?.highlight?.judul ?? ''}
                        image={blogData?.highlight?.upload_thumbnail ?? ''}
                        link={`/blog/${blogData?.highlight?.id}`}
                        created_at={blogData?.highlight?.created_at ?? ''}
                        category={blogData?.highlight?.kategori?.nama_kategori ?? ''}
                        height='400px'
                    />
                </div>
                {/* Small cards */}
                <div className='col-md-6'>
                    <div className='row gap-4 h-100 d-flex flex-column justify-content-between align-items-center'>
                        {blogData?.news?.map((item: BlogItemType, index: number) => (
                            <div className='col' key={index}>
                                <Link to={`/blog/${item.id}`} className='text-decoration-none'>
                                    <Card className={clsx('border-0 shadow-sm rounded-4 overflow-hidden h-auto card-hover', styles.blogCard)}>
                                        <div className='row g-0 h-100'>
                                            <div className='col-5'>
                                                <div className='position-relative h-100'>
                                                    <Card.Img 
                                                        src={item.upload_thumbnail ?? ''} 
                                                        alt="Blog post"
                                                        className='h-100'
                                                        style={{ objectFit: 'cover', minHeight: '200px' }}
                                                    />
                                                    {/* Dark overlay */}
                                                    <div 
                                                        className='position-absolute top-0 start-0 w-100 h-100' 
                                                        style={{ 
                                                            background: 'rgba(0, 0, 0, 0.2)',
                                                            zIndex: 1
                                                        }}
                                                    ></div>
                                                    
                                                    {/* Date tag */}
                                                    <div className='position-absolute top-0 start-0 m-2' style={{ zIndex: 2 }}>
                                                        <div className='bg-primary text-white p-2 rounded-2 small fw-bold' style={{ width: '42px', height: '42px' }}>
                                                            <div className='fw-bold fs-5 m-0 text-center' style={{ lineHeight: '1' }}> 
                                                                {item.created_at ? new Date(item.created_at).getDate() : '-'}
                                                            </div>
                                                            <div className='fw-normal fs-7 m-0 text-center'>
                                                                {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short' }) : '-'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='col-7'>
                                                <Card.Body className='d-flex flex-column h-100'>
                                                    <div className='d-flex align-items-center gap-2 mb-2'>
                                                        <i className='bi bi-tag text-primary'></i>
                                                        <span className='small text-muted'>{item.kategori?.nama_kategori}</span>
                                                    </div>
                                                    <h4
                                                        className='fw-bold mb-2 text-dark'
                                                        style={{ 
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            lineHeight: '1.4'
                                                        }}
                                                    >
                                                        {item.judul}
                                                    </h4>
                                                    <div className='d-flex mt-auto'>
                                                        <Link to={`/blog/${item.id}`} className='text-primary fs-4 fw-bold d-flex align-items-center gap-2 card-link'>
                                                            Selengkapnya 
                                                            <i className='bi bi-arrow-right fs-4 text-primary arrow-hover'></i>
                                                        </Link>
                                                    </div>
                                                </Card.Body>
                                            </div>
                                        </div>
                                    </Card>
                                </Link>
                            </div>
                        ))}
                    </div>  
                </div>
            </div>
        </Container>
    )
}

export default Blog;